import classnames from 'classnames';
import {InputError} from 'components/atoms/FormElements/InputError';
import {Label} from 'components/atoms/FormElements/Label';

import {Icon} from 'components/atoms/Media/Icon';
import {getFormattedMessage} from 'features/services/utils/language-helper';
import * as React from 'react';
import {useEffect, useRef, useState} from 'react';

import './style.scss';

export interface InputProps {
    name: string;
    id: string;
    uniqName?: string;
    className?: string;

    type: string;
    textarea?: boolean;
    numericOnly?: boolean;

    valid?: boolean;
    disabled?: boolean;
    pristine?: boolean;
    pattern?: string;

    innerRef?: React.Ref<HTMLInputElement | HTMLTextAreaElement>;
    value?: string;
    defaultValue?: string;
    placeholder?: string;
    tabIndex?: number;
    autoCapitalize?: string;
    messages: string[];

    onBlur?: (value: string, name: string) => void;
    onFocus?: (value?: string) => void;
    onChange?: (value: string, name: string) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    eventOnChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    eventOnBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    'data-test-id'?: string;
}

export const Input: React.FunctionComponent<InputProps> = ({
    name, id: _id, uniqName, className,
    type, textarea, numericOnly,
    valid, disabled, pristine, pattern,
    innerRef: _innerRef, value, defaultValue='', placeholder, tabIndex, autoCapitalize, messages,
    onBlur, onFocus, onChange, onKeyDown, eventOnChange, eventOnBlur, ...rest
}) => {
    const [isPristine, setIsPristine] = useState<boolean>((typeof pristine === 'undefined') ? true : pristine);
    const [isActive, setIsActive] = useState<boolean>(false);
    const [_inputValue, _setInputValue] = useState<string>(value ? value : defaultValue);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [largePlaceholder, setLargePlaceholder] = useState<boolean>((value && value.length > 0) ? false : true);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ref is used for both input and textarea elements
    const inputRef = useRef<any>(null);

    const isInvalid = () => {
        return typeof valid !== 'undefined' && !valid;
    };

    useEffect(() => {
        if(largePlaceholder && value && value.length > 0) {
            setLargePlaceholder(false);
        }

        if(!largePlaceholder && value && value.length === 0) {
            setLargePlaceholder(true);
        }

    }, [value]);

    useEffect(() => {
       setIsPristine(pristine ? pristine : true);
    }, [pristine]);

    const internalOnBlur = () => {
        setIsActive(false);
        setIsPristine(false);

        if(inputRef.current === null) {
            console.error('Input reference not defined', name);
            return;
        }
        if(!value) {
            setLargePlaceholder(true);
        }

        if(!inputRef.current) return;

        if (onBlur) onBlur(inputRef.current.value, inputRef.current.name);
    };

    const internalOnFocus = () => {
        setIsActive(true);
        setLargePlaceholder(false);
        const _inputValue = inputRef.current && inputRef.current.value;

        if (onFocus) onFocus(value);
    };

    const internalOnChange = (_e) => {
        if(inputRef.current === null) {
            console.error('Input reference not defined', name);
            return;
        }

        let inputValue = inputRef.current && inputRef.current.value;

        if(numericOnly && inputValue) {
            inputValue = inputValue.replace(/[^\d]+/g,'');
        }

        if (onChange) onChange(inputValue ? inputValue : '', name);
    };

    const togglePassword = () => {
        setShowPassword(!showPassword);
    };

    const classes = classnames('input-field', className,  {
        'input-field--valid': valid,
        'input-field--invalid': isInvalid(),
        'input-field--dirty': !isPristine,
        'input-field--pristine': isPristine,
        'input-field--active': isActive,
        'input-field--disabled': disabled,
    });

    const inputName = (uniqName) ? `${uniqName}-${name}` : `${name}`;

    return (
        <React.Fragment>
            <div className={classes}>
                {placeholder && <Label htmlFor={inputName} large={largePlaceholder}>{placeholder}</Label>}
                {textarea &&
                    <textarea
                        name={inputName}
                        id={inputName}
                        data-test-id={rest["data-test-id"]}
                        value={value}
                        disabled={disabled}
                        onFocus={internalOnFocus}
                        onBlur={eventOnBlur ? eventOnBlur : internalOnBlur}
                        onChange={eventOnChange ? eventOnChange : internalOnChange}
                        onKeyDown={onKeyDown}
                        ref={inputRef}
                        tabIndex={tabIndex}
                    />
                }
                {!textarea &&
                    <input
                        type={(type === "password" && showPassword) ? "text" : type}
                        name={name}
                        id={name}
                        data-test-id={rest["data-test-id"]}
                        disabled={disabled}
                        pattern={pattern}
                        value={value}
                        onFocus={internalOnFocus}
                        onBlur={eventOnBlur ? eventOnBlur : internalOnBlur}
                        onChange={eventOnChange ? eventOnChange : internalOnChange}
                        onKeyDown={onKeyDown}
                        ref={inputRef}
                        autoCapitalize={autoCapitalize ? autoCapitalize : 'on'}
                        tabIndex={tabIndex}
                        formNoValidate
                    />
                }
                <span className="input-field__validation-icon">
                    { type !== "password" && valid && !isPristine && <Icon name="input-ok" /> }
                    { type !== "password" && isInvalid() && !isPristine && <Icon name="input-error" /> }
                    { type === "password" && showPassword && <Icon name="password-hide" label={getFormattedMessage('jm.a11y.hidePassword')} onClick={togglePassword} /> }
                    { type === "password" && !showPassword && <Icon name="password-show" label={getFormattedMessage('jm.a11y.showPassword')} onClick={togglePassword} /> }
                </span>
                {!isPristine && <InputError visible={messages.length > 0}>{messages[0]}</InputError>}
            </div>

        </React.Fragment>
    )
};
